import { useState, useEffect, lazy, Suspense } from 'react';
import Home from './pages/Home';
// Import directo (no lazy): el HTML estático de la landing ya está pintado y
// un fallback de Suspense lo borraría un instante al montar.
import LandingPage from './pages/LandingPage';
import { findLanding } from './seo/landings';
import { getIdToken, consumeGoogleRedirectResult } from './services/auth';
import { esRutaAdmin, obtenerRutaAdmin } from './lib/adminPath';

const AdminDashboard = lazy(() =>
  import('./components/admin/AdminDashboard').then(m => ({ default: m.AdminDashboard }))
);
const Accesibilidad = lazy(() => import('./pages/Accesibilidad'));
import { GenderProvider, useGender } from './contexts/GenderContext';
import { GenderToggle } from './components/GenderToggle';
import { CookieBanner } from './components/CookieBanner';
import { LegalModal } from './components/legal/LegalModal';
import { SEO } from './components/SEO';
import { useHtmlLang } from './hooks/useHtmlLang';

function LegalPageWrapper({ tipo }: { tipo: 'privacidad' | 'terminos' | 'cookies' }) {
  // Mantén <html lang="..."> sincronizado para WCAG 3.1.1 — Helmet en SEO podría
  // sobrescribir, así que también lo refuerza el hook.
  useHtmlLang();
  const seoMap: Record<string, { title: string; desc: string; path: string }> = {
    privacidad: { title: 'home.legalTitlePrivacy', desc: 'home.legalTitlePrivacy', path: '/privacidad' },
    terminos:   { title: 'home.legalTitleTerms',   desc: 'home.legalTitleTerms',   path: '/terminos' },
    cookies:    { title: 'home.legalTitleCookies', desc: 'home.legalTitleCookies', path: '/cookies' },
  };
  const meta = seoMap[tipo];
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--page-bg)' }}>
      <SEO path={meta.path} titleKey={meta.title} descriptionKey={meta.desc} noindex />
      <main className="max-w-4xl mx-auto px-4 py-8">
        <nav aria-label="Migas de pan">
          <a href="/" className="inline-block mb-4 text-sm text-[#7A5F0A] underline hover:no-underline">← Volver al inicio</a>
        </nav>
        <LegalModal tipo={tipo} onClose={() => { window.location.href = '/'; }} />
      </main>
    </div>
  );
}

function AppInner() {
  const { genero } = useGender();
  // Las landings SEO se resuelven en el primer render (no en un efecto) para
  // no montar la Home, con su tracking y sus modales, en /bodas y similares.
  const [landing] = useState(() => (typeof window !== 'undefined' ? findLanding(window.location.pathname) : undefined));
  const [route, setRoute] = useState<'home' | 'admin' | 'legal' | 'accesibilidad' | 'landing'>(() => (landing ? 'landing' : 'home'));
  const [legalTipo, setLegalTipo] = useState<'privacidad' | 'terminos' | 'cookies'>('privacidad');
  const [adminToken, setAdminToken] = useState('');

  useEffect(() => {
    // If we just came back from Google Sign-In redirect (mobile), pick up the user.
    consumeGoogleRedirectResult().then(user => {
      if (user?.id) window.location.reload(); // refresh so localStorage user is picked up everywhere
    }).catch(() => {});
  }, []);

  useEffect(() => {
    // Legacy calendar links used /?admin=1&order=<id>; redirect to /admin?order=<id>
    const qs = new URLSearchParams(window.location.search);
    if (window.location.pathname === '/' && qs.get('admin') === '1' && qs.get('order')) {
      const orderId = qs.get('order')!;
      obtenerRutaAdmin().then(ruta => { if (ruta) window.location.replace(`${ruta}?order=${encodeURIComponent(orderId)}`); });
      return;
    }
    // La ruta del panel no está escrita en el código: se reconoce por su hash
    // (ver src/lib/adminPath.ts). /admin ya no es el panel.
    let refreshInterval: ReturnType<typeof setInterval> | undefined;
    let cancelado = false;
    esRutaAdmin(window.location.pathname).then(esAdmin => {
      if (!esAdmin || cancelado) return;
      setRoute('admin');
      const meta = document.createElement('meta');
      meta.name = 'robots';
      meta.content = 'noindex, nofollow';
      document.head.appendChild(meta);
      getIdToken().then(token => { if (token) setAdminToken(token); });
      // Auto-refresh token every 45 min (Firebase tokens expire after 1h)
      refreshInterval = setInterval(() => {
        getIdToken().then(token => { if (token) setAdminToken(token); });
      }, 45 * 60 * 1000);
    });
    // Páginas legales accesibles directamente por URL
    const path = window.location.pathname;
    if (path === '/privacidad' || path === '/privacy') { setRoute('legal'); setLegalTipo('privacidad'); }
    else if (path === '/terminos' || path === '/terms') { setRoute('legal'); setLegalTipo('terminos'); }
    else if (path === '/cookies') { setRoute('legal'); setLegalTipo('cookies'); }
    else if (path === '/accesibilidad' || path === '/accessibility') { setRoute('accesibilidad'); }
    return () => { cancelado = true; if (refreshInterval) clearInterval(refreshInterval); };
  }, []);

  const currentUser = (() => {
    try { return JSON.parse(localStorage.getItem('funcomunion_user') || 'null'); } catch { return null; }
  })();
  const isAdmin = currentUser?.rol === 'admin';

  if (route === 'admin' && !isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--page-bg)' }}>
        <div className="text-center p-8">
          <h1 className="text-2xl font-bold mb-4" style={{ color: 'var(--page-heading)' }}>Acceso no autorizado</h1>
          <p className="text-[#6B7280] mb-6">Necesitas permisos de administrador para acceder a esta área.</p>
          <a href="/" className="px-6 py-3 btn-nacre rounded-full">
            Volver al inicio
          </a>
        </div>
      </div>
    );
  }

  return (
    // Landings con el tema neutro: es el que lleva su HTML estático, así no
    // cambia de color al montar React (el género por defecto es 'nina').
    <div data-genero={route === 'landing' ? undefined : genero}>
      {route === 'home' && <Home />}
      {route === 'landing' && landing && <LandingPage landing={landing} />}
      {route === 'legal' && <LegalPageWrapper tipo={legalTipo} />}
      {route === 'accesibilidad' && (
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><span className="text-[#6B7280]">Cargando...</span></div>}>
          <Accesibilidad />
        </Suspense>
      )}
      {route === 'admin' && isAdmin && adminToken && (
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><span className="text-[#6B7280]">Cargando...</span></div>}>
          <AdminDashboard
            adminToken={adminToken}
            onClose={() => { window.location.href = '/'; }}
          />
        </Suspense>
      )}
      {route === 'admin' && isAdmin && !adminToken && (
        <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--page-bg)' }}>
          <div className="text-center p-8 max-w-md">
            <h1 className="text-xl font-semibold mb-2" style={{ color: 'var(--page-heading)' }}>Recuperando sesión...</h1>
            <p className="text-sm text-[#6B7280] mb-6">Si no carga en unos segundos, vuelve a iniciar sesión.</p>
            <a href="/" className="px-6 py-3 btn-nacre rounded-full">Ir al inicio</a>
          </div>
        </div>
      )}
      {route !== 'landing' && <GenderToggle />}
      <CookieBanner />
    </div>
  );
}

function App() {
  return (
    <GenderProvider>
      <AppInner />
    </GenderProvider>
  );
}

export default App;
